import { dayjs } from "@/utils/dayjs";
import { Dayjs } from "dayjs";

/**
 * The subset of fields the status rules need, loose enough to accept both shapes that carry them:
 * the form values (`TInformationForm`, dates as Dayjs) and the list/detail DTOs (dates as strings).
 */
export type TInformationStatusInput = {
  type?: string | null;
  isDraft?: boolean | null;
  startDate?: Dayjs | string | null;
  /** Absent on the DTOs — only the form has the switch; absent never reads as "switched off". */
  enableStartDate?: boolean;
  emailBroadCastHistories?: unknown[] | null;
};

export const INFORMATION_STATUS = {
  DRAFT: "下書き",
  PUBLISHED: "公開済み",
  UNPUBLISHED: "未公開",
} as const;

export type TInformationStatus = (typeof INFORMATION_STATUS)[keyof typeof INFORMATION_STATUS];

/** Derives the display status of an information record from its saved values. */
export const getInformationStatus = (data: TInformationStatusInput): TInformationStatus => {
  const { type, isDraft, startDate, enableStartDate, emailBroadCastHistories } = data;

  if (!type || isDraft) {
    return INFORMATION_STATUS.DRAFT;
  }
  // A switched-off start date is treated as absent, whatever value the picker still holds.
  if (!startDate || enableStartDate === false) {
    return INFORMATION_STATUS.PUBLISHED;
  }
  if (emailBroadCastHistories && emailBroadCastHistories.length > 0) {
    return INFORMATION_STATUS.PUBLISHED;
  }
  return dayjs(startDate).isSameOrBefore(dayjs(), "day") ? INFORMATION_STATUS.PUBLISHED : INFORMATION_STATUS.UNPUBLISHED;
};

/** Once published, 公開開始日 is locked: the notification is already out. */
export const isStartDateLocked = (savedData?: TInformationStatusInput): boolean => {
  if (!savedData) return false;
  return getInformationStatus(savedData) === INFORMATION_STATUS.PUBLISHED;
};

/** 公開終了日 is locked only once it has elapsed; an upcoming end date stays editable. */
export const isEndDateLocked = (savedEndDate?: Dayjs | null): boolean => {
  if (!savedEndDate) return false;
  return dayjs(savedEndDate).isBefore(dayjs(), "day");
};
